<script lang="ts" setup>
import { computed, defineEmits, defineProps, ref } from 'vue'
import { mdiCheckCircleOutline, mdiClose, mdiDotsHorizontal } from '@mdi/js'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { getDateFormatted } from '@/services/date'
import { useImage } from '@/shared/composables/image'
import { eventApi } from '@/enitites/event'
import { IEventPreview } from '@/enitites/event/model/types'
import { useRouter } from 'vue-router/composables'

const { getEventImg } = useImage()
const router = useRouter()

const props = defineProps<{
  canEdit?: boolean
  event: IEventPreview
}>()

const emit = defineEmits<{
  (e: 'reloadEvents'): void
}>()

const loading = ref(false)
const eventDate = computed(() => getDateFormatted(props.event.dateStart))

function handleClickEvent() {
  router.push({
    name: 'Event',
    params: { id: `${props.event.id}` },
  })
}

async function publishEvent() {
  loading.value = true
  const resp = await eventApi.publishEvent(props.event.id)
  if (resp) {
    props.event.status = 'published'
  }
  loading.value = false
}

async function deleteEvent() {
  const resp = await eventApi.deleteEvent(props.event.id)
  if (resp) {
    emit('reloadEvents')
  }
}
</script>

<template>
  <button
    class="event"
    :class="{
      'event-with-feedback': event.canLeaveFeedback && event.canLike,
    }"
    @click="handleClickEvent"
  >
    <div class="information">
      <v-avatar
        class="event-image"
        size="72"
        tile
      >
        <v-img :src="getEventImg(event.image, 200)" />
      </v-avatar>
      <div class="event-title">
        <div class="event-title-top">
          <span class="event-date">{{ eventDate }}</span>
          <span class="event-title-divider" />
          <span
            v-if="event.locationAddress"
            class="event-location"
          >
            {{ event.locationAddress }}
          </span>
        </div>
        <span class="name">{{ event.title }}</span>
      </div>
    </div>

    <div
      v-if="canEdit"
      class="options"
    >
      <div @click.stop="">
        <v-menu offset-y>
          <template v-slot:activator="{ on }">
            <v-icon
              class="menu-icon"
              v-on="on"
            >
              {{ mdiDotsHorizontal }}
            </v-icon>
          </template>
          <v-list>
            <v-list-item @click="deleteEvent">
              <v-list-item-title>
                <v-icon>
                  {{ mdiClose }}
                </v-icon>
                Remove event
              </v-list-item-title>
            </v-list-item>

            <v-list-item @click="publishEvent">
              <v-list-item-title>
                <v-icon>
                  {{ mdiCheckCircleOutline }}
                </v-icon>
                Publish event
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </div>
    </div>
  </button>
</template>

<style lang="scss" scoped>
@import '@/assets/style/mixins';

.event {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  background-color: var(--color-white);
  border-radius: var(--border-radius-rounded);
  cursor: pointer;
  gap: 10px;

  &.event-with-feedback {
    @media (max-width: 600px) {
      gap: 20px;
      align-items: start;
      flex-direction: column;
    }
  }
}

.information {
  display: flex;
  gap: 12px;
  align-items: center;
}

.event-title {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &-top {
    @include font-body2;

    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: flex-start;
  }

  &-divider {
    align-self: stretch;
    width: 1px;
    background-color: var(--color-dark-800);
  }

  .name {
    font-weight: var(--font-weight-bold);
    text-align: left;
  }
}

.options {
  display: flex;

  .menu-icon,
  .doc-progress-icon {
    color: var(--color-dark-800);
  }

  .doc-progress-loader {
    margin-right: 40px;
  }

  .doc-progress-icon {
    margin-right: 40px;
  }
}
</style>
